import type { ElementType, ReactNode } from 'react';
import styles from './Text.module.css';

/**
 * Every piece of text in the application. Design system § 5.1 defines twelve
 * scale steps; this is their only entry point.
 *
 * `as` and `variant` are independent on purpose: heading level is a document
 * and accessibility concern, type scale is a visual one, and § 5.2's "maximum
 * two display levels per screen" is not the same statement as "two <h1>s".
 * `<Text as="h1" variant="display-lg">` is the normal case.
 */

export type TextVariant =
  | 'display-xl'
  | 'display-lg'
  | 'heading'
  | 'subheading'
  | 'body'
  | 'body-sm'
  | 'label'
  | 'caption'
  | 'button'
  | 'numeric-display'
  | 'numeric-inline'
  | 'token-lg';

/**
 * The § 3.2 roles text is allowed to take. Not a colour: a role.
 *
 * `gradient` is § 5.3's treatment — `grad/text` as the letterforms' own
 * colour. It is a tone rather than a second axis because that is exactly what
 * it is: the brand as the text's colour. It applies only to the five display
 * steps § 5.3 permits, and is ignored on the rest.
 */
export type TextTone =
  | 'default'
  | 'muted'
  | 'inverse'
  | 'success'
  | 'near-miss'
  | 'error'
  | 'gradient';

interface TextProps {
  as?: ElementType;
  variant?: TextVariant;
  tone?: TextTone;
  children: ReactNode;
  id?: string;
}

export function Text({
  as: Component = 'span',
  variant = 'body',
  tone = 'default',
  children,
  id,
}: TextProps) {
  return (
    <Component
      id={id}
      className={`${styles.text} type-${variant}`}
      data-variant={variant}
      data-tone={tone}
    >
      {children}
    </Component>
  );
}
